<script setup>
import { ref, onMounted } from 'vue';
import { getSession } from '../utils/auth';
import { getGroupFromSession, uploadRoutesJson, uploadImages } from '../utils/userdata';

const emit = defineEmits(['close']);

// ── State ────────────────────────────────────────────────────
const group = ref(null);
const bucket = import.meta.env.VITE_ROUTE_USERDATA;

const routesFile = ref(null);
const routesStatus = ref(null); // null | 'uploading' | 'success' | { error }

const imageFiles = ref([]);
const imagesStatus = ref(null); // null | { done, total } | 'success' | { error }

onMounted(async () => {
  try {
    const session = await getSession();
    group.value = getGroupFromSession(session);
  } catch {
    // session already checked by parent; group stays null
  }
});

// ── Routes upload ────────────────────────────────────────────
const onRoutesFileChange = (e) => {
  routesFile.value = e.target.files[0] ?? null;
  routesStatus.value = null;
};

const handleRoutesUpload = async () => {
  if (!routesFile.value) return;
  routesStatus.value = 'uploading';
  try {
    const text = await routesFile.value.text();
    JSON.parse(text); // validate JSON before uploading
    await uploadRoutesJson(text);
    routesStatus.value = 'success';
  } catch (err) {
    routesStatus.value = { error: err.message || 'Upload failed' };
  }
};

// ── Images upload ────────────────────────────────────────────
const onImagesChange = (e) => {
  imageFiles.value = Array.from(e.target.files);
  imagesStatus.value = null;
};

const handleImagesUpload = async () => {
  if (!imageFiles.value.length) return;
  imagesStatus.value = { done: 0, total: imageFiles.value.length };
  try {
    await uploadImages(imageFiles.value, (done, total) => {
      imagesStatus.value = { done, total };
    });
    imagesStatus.value = 'success';
  } catch (err) {
    imagesStatus.value = { error: err.message || 'Upload failed' };
  }
};

const routesKey = () => group.value ? `groups/${group.value}/routes.json` : '—';
const imagesPrefix = () => group.value ? `groups/${group.value}/images/` : '—';
</script>

<template>
  <div class="auth-page">
    <div class="auth-card upload-card">
      <h2>Upload User Data</h2>

      <div
        v-if="!group"
        class="upload-hint error-text"
      >
        Your account is not assigned to a group. Contact an administrator.
      </div>

      <template v-else>
        <p class="upload-hint">
          Bucket: <code>{{ bucket }}</code><br>
          Group: <code>{{ group }}</code>
        </p>

        <!-- Routes JSON ───────────────────────────── -->
        <section class="upload-section">
          <h3>Routes data</h3>
          <p class="upload-key">
            Key: <code>{{ routesKey() }}</code>
          </p>
          <div class="form-group">
            <label for="routes-file">Select routes.json</label>
            <input
              id="routes-file"
              type="file"
              accept=".json,application/json"
              :disabled="routesStatus === 'uploading'"
              @change="onRoutesFileChange"
            >
          </div>
          <button
            class="btn-primary"
            :disabled="!routesFile || routesStatus === 'uploading'"
            @click="handleRoutesUpload"
          >
            {{ routesStatus === 'uploading' ? 'Uploading…' : 'Upload routes.json' }}
          </button>
          <p
            v-if="routesStatus === 'success'"
            class="status-ok"
          >
            ✓ Uploaded successfully
          </p>
          <p
            v-else-if="routesStatus?.error"
            class="status-err"
          >
            {{ routesStatus.error }}
          </p>
        </section>

        <!-- Images ────────────────────────────────── -->
        <section class="upload-section">
          <h3>Images</h3>
          <p class="upload-key">
            Prefix: <code>{{ imagesPrefix() }}</code>
          </p>
          <div class="form-group">
            <label for="image-files">Select images</label>
            <input
              id="image-files"
              type="file"
              accept="image/*"
              multiple
              :disabled="typeof imagesStatus === 'object' && imagesStatus?.total"
              @change="onImagesChange"
            >
          </div>
          <button
            class="btn-primary"
            :disabled="!imageFiles.length || (typeof imagesStatus === 'object' && imagesStatus?.total && imagesStatus?.done < imagesStatus?.total)"
            @click="handleImagesUpload"
          >
            <template v-if="typeof imagesStatus === 'object' && imagesStatus?.total">
              Uploading {{ imagesStatus.done }} / {{ imagesStatus.total }}…
            </template>
            <template v-else>
              Upload {{ imageFiles.length || '' }} image{{ imageFiles.length !== 1 ? 's' : '' }}
            </template>
          </button>
          <p
            v-if="imagesStatus === 'success'"
            class="status-ok"
          >
            ✓ All images uploaded
          </p>
          <p
            v-else-if="imagesStatus?.error"
            class="status-err"
          >
            {{ imagesStatus.error }}
          </p>
        </section>
      </template>

      <button
        class="btn-secondary upload-close"
        @click="emit('close')"
      >
        Close
      </button>
    </div>
  </div>
</template>

<style scoped>
.upload-card {
  max-width: 520px;
  gap: 1.2rem;
}

.upload-hint {
  font-size: 0.85rem;
  color: #555;
  line-height: 1.5;
}

.upload-section {
  border-top: 1px solid #e0e0e0;
  padding-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.upload-section h3 {
  margin: 0;
  font-size: 1rem;
}

.upload-key {
  margin: 0;
  font-size: 0.82rem;
  color: #555;
}

.upload-close {
  margin-top: 0.5rem;
  align-self: flex-end;
}

.status-ok {
  color: #2a7a3b;
  font-size: 0.88rem;
  margin: 0;
}

.status-err {
  color: #b00020;
  font-size: 0.88rem;
  margin: 0;
}

.error-text {
  color: #b00020;
}
</style>
